import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import {
  defaultPedidosConfig,
  loadPedidosConfig,
  PedidosConfigState,
  resetPedidosConfig,
  setPedidosConfigValue,
} from '../../../../crud/localDB/pedidosConfig';
import useSiiConfigured from '../../../../crud/management_api/useSiiConfigured';
import './PedidosConfig.scss';

const PedidosConfig: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<PedidosConfigState>(defaultPedidosConfig);

  const localId = useMemo(() => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return Number(u.local_id) || 0;
  }, []);
  const siiConfigured = useSiiConfigured(localId);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setConfig(await loadPedidosConfig());
    } catch (error) {
      console.error('Error loading pedidos config:', error);
      toast.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key: keyof PedidosConfigState) => {
    const newValue = !config[key];
    
    try {
      await setPedidosConfigValue(key, newValue);
      setConfig(prev => ({ ...prev, [key]: newValue }));
      toast.success('Configuración actualizada');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar la configuración');
    }
  };

  const handleReset = async () => {
    try {
      await resetPedidosConfig();
      
      setConfig(defaultPedidosConfig);
      toast.success('Configuración restablecida');
    } catch (error) {
      console.error('Error resetting config:', error);
      toast.error('Error al restablecer la configuración');
    }
  };

  if (loading) {
    return <LoadingSpinner text="Cargando configuración" transparent />;
  }

  return (
    <div className="pedidos-config-container">
      <div className="mp-page-header-container">
        <h2 className="title">Configuración de Pedidos</h2>
        <p className="subtitle">Personaliza el comportamiento del tomador de pedidos</p>
      </div>

      <div className="config-sections">
        {/* Sorting Section */}
        <div className="cfg-section">
          <div className="cfg-section__title">Ordenamiento de Elementos</div>
          <div className="cfg-section__desc">
            Controla cómo se ordenan las categorías y productos en el tomador de pedidos
          </div>

          <div className="cfg-switch-row">
            <div className="cfg-switch-row__label">
              <strong>Ordenar productos alfabéticamente</strong>
              <small>Los productos dentro de cada categoría se mostrarán en orden alfabético</small>
            </div>
            <label className="cfg-switch">
              <input
                type="checkbox"
                checked={config.sortItemsAlphabetically}
                onChange={() => handleToggle('sortItemsAlphabetically')}
              />
              <span className="slider">
                <span className="knob"></span>
              </span>
            </label>
          </div>

          <div className="cfg-switch-row">
            <div className="cfg-switch-row__label">
              <strong>Ordenar categorías alfabéticamente</strong>
              <small>Las categorías se mostrarán en orden alfabético en lugar del orden definido en el menú</small>
            </div>
            <label className="cfg-switch">
              <input
                type="checkbox"
                checked={config.sortCategoriesAlphabetically}
                onChange={() => handleToggle('sortCategoriesAlphabetically')}
              />
              <span className="slider">
                <span className="knob"></span>
              </span>
            </label>
          </div>
        </div>

        {/* Modal Behavior Section */}
        <div className="cfg-section">
          <div className="cfg-section__title">Comportamiento del Selector</div>
          <div className="cfg-section__desc">
            Define cuándo se debe mostrar el modal de selección al agregar productos
          </div>

          <div className="cfg-switch-row">
            <div className="cfg-switch-row__label">
              <strong>Siempre mostrar selector</strong>
              <small>
                Muestra el modal de selección incluso para productos que solo tienen opciones opcionales.
                Si está desactivado, estos productos se agregan directamente al carrito
              </small>
            </div>
            <label className="cfg-switch">
              <input
                type="checkbox"
                checked={config.alwaysShowSelectorModal}
                onChange={() => handleToggle('alwaysShowSelectorModal')}
              />
              <span className="slider">
                <span className="knob"></span>
              </span>
            </label>
          </div>
        </div>

        {/* Cancellation Behavior Section */}
        <div className="cfg-section">
          <div className="cfg-section__title">Comandas de Eliminación</div>
          <div className="cfg-section__desc">
            Define cómo se notifican a cocina las eliminaciones, anulaciones y reducciones de productos
          </div>

          <div className="cfg-switch-row">
            <div className="cfg-switch-row__label">
              <strong>Notificar eliminaciones automáticamente</strong>
              <small>
                Envía comandas de eliminación a cocina por defecto y evita preguntar al usuario en cada anulación.
              </small>
            </div>
            <label className="cfg-switch">
              <input
                type="checkbox"
                checked={config.autoPrintCancellationComandas}
                onChange={() => handleToggle('autoPrintCancellationComandas')}
              />
              <span className="slider">
                <span className="knob"></span>
              </span>
            </label>
          </div>
        </div>
        {/* Double Click Feature Section */}
        <div className="cfg-section">
          <div className="cfg-section__title">Etiquetado de Tiempo de Servicio</div>
          <div className="cfg-section__desc">
            Habilita la funcionalidad de doble clic para marcar productos como entrada o fondo
          </div>

          <div className="cfg-switch-row">
            <div className="cfg-switch-row__label">
              <strong>Etiquetado con doble clic</strong>
              <small>
                Al hacer doble clic en un producto del carrito, se etiqueta como (ENTRADA) → (FONDO) → sin etiqueta.
                Útil para identificar rápidamente el tiempo de servicio de cada platillo
              </small>
            </div>
            <label className="cfg-switch">
              <input
                type="checkbox"
                checked={config.enableDoubleClickCourse}
                onChange={() => handleToggle('enableDoubleClickCourse')}
              />
              <span className="slider">
                <span className="knob"></span>
              </span>
            </label>
          </div>
        </div>

        {/* SII / Facturación Section */}
        <div className="cfg-section">
          <div className="cfg-section__title">Facturación SII</div>
          <div className="cfg-section__desc">
            Acelera la emisión de documentos tributarios desde la pantalla de cobro
          </div>

          <div className="cfg-switch-row">
            <div className="cfg-switch-row__label">
              <strong>Imprimir boleta con un clic</strong>
              <small>
                Muestra un botón para generar e imprimir la boleta electrónica (SII) en un solo paso,
                disponible en recibos pagados.
                {siiConfigured === false && (
                  <span className="cfg-inline-note">
                    {' '}Requiere configurar la integración SII en Configuración → Integraciones → SII.
                  </span>
                )}
              </small>
            </div>
            <label className={`cfg-switch ${siiConfigured !== true ? 'is-disabled' : ''}`}>
              <input
                type="checkbox"
                checked={config.enableQuickBoletaPrint}
                disabled={siiConfigured !== true}
                onChange={() => handleToggle('enableQuickBoletaPrint')}
              />
              <span className="slider">
                <span className="knob"></span>
              </span>
            </label>
          </div>
        </div>

        {/* Reset Button */}
        <div className="config-footer">
          <button className="btn-reset" onClick={handleReset}>
            Restablecer Configuración
          </button>
        </div>
      </div>
    </div>
  );
};

export default PedidosConfig;
